import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown, X } from "lucide-react";

/**
 * Multi-select filter (checkbox dropdown) avec persistance côté appelant.
 *
 * Props:
 *  - label: texte de l'étiquette du trigger quand aucune option n'est cochée.
 *  - selected: string[]
 *  - onChange: (next: string[]) => void
 *  - options: { value, label, group? }[]
 *  - testId: data-testid du bouton trigger
 *  - searchable: afficher un champ de recherche si la liste est longue (default auto > 8)
 *  - allLabel: texte quand toutes les options sont sélectionnées (optionnel)
 */
export const MultiSelectFilter = ({
  label,
  selected = [],
  onChange,
  options = [],
  testId,
  searchable,
  allLabel,
  widthClass = "min-w-[200px]",
}) => {
  const [search, setSearch] = useState("");

  const showSearch = searchable ?? options.length > 8;

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const opt of filteredOptions) {
      const key = opt.group || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(opt);
    }
    return Array.from(map.entries());
  }, [filteredOptions]);

  const toggle = (value) => {
    if (selected.includes(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };

  const count = selected.length;
  const all = count === 0 || count === options.length;

  const triggerLabel = (() => {
    if (count === 0) return label;
    if (count === options.length && allLabel) return allLabel;
    if (count === 1) {
      const opt = options.find((o) => o.value === selected[0]);
      return opt?.label || label;
    }
    return label;
  })();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`justify-between flex-1 ${widthClass}`}
          data-testid={testId}
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            {count > 0 && !all && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-semibold">
                {count}
              </Badge>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-96 overflow-y-auto">
        {showSearch && (
          <div className="p-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="h-8 text-sm"
              data-testid={testId ? `${testId}-search` : undefined}
              onKeyDown={(e) => {
                // Éviter que le DropdownMenu ne vole le focus (A-Z)
                e.stopPropagation();
              }}
            />
          </div>
        )}
        <div className="flex items-center justify-between px-2 pb-1">
          <DropdownMenuLabel className="px-0 py-0 text-[10px] uppercase text-muted-foreground">
            {count > 0 ? `${count} sélectionné${count > 1 ? "s" : ""}` : "Aucun filtre"}
          </DropdownMenuLabel>
          {count > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }}
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="h-3 w-3 mr-1" /> Effacer
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />
        {grouped.length === 0 && (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
            Aucun résultat.
          </div>
        )}
        {grouped.map(([group, opts]) => (
          <div key={group || "__default"}>
            {group && (
              <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground px-2 py-1">
                {group}
              </DropdownMenuLabel>
            )}
            {opts.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
                data-testid={testId ? `${testId}-opt-${opt.value}` : undefined}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MultiSelectFilter;
